import React, { useEffect, useState } from 'react';
import { Select, Typography } from 'antd';

import { Associative } from '../../../types';
import { useClient, useUser } from '../../../contexts';
import { CUSTOMER_TYPES } from 'src/constants';
import {
  flattenGroupTree,
  groupTagValue,
  indentLabel,
} from 'src/helpers/groupTree';
import { eventNetworks } from 'src/contexts/User/scope';

interface TagsProps {
  id: string;
  name: string;
  placeholder: string;
  defaultValue?: Array<string>;
  options?: Array<string>;
  /** Responsable de communauté : propose aussi « Tout le Réseau » (évènements) */
  withNetworks?: boolean;
  /** Ne propose que ce Réseau et ses groupes (communauté qui signe) */
  networkId?: string;
  onChange?: (tags: Array<string>) => void;
}

const { Text } = Typography;

export const Tags: React.FC<TagsProps> = ({
  id,
  name,
  placeholder,
  defaultValue,
  options = [],
  withNetworks = false,
  networkId,
  onChange,
}) => {
  const { get } = useClient();
  const [optionsValues, setOptionsValues] = useState<any>([]);
  /* Valeurs affichées : suivies ici pour pouvoir retirer des cibles (Réseau restreint) */
  const [selected, setSelected] = useState<Array<string>>(defaultValue ?? []);
  const { user, scope } = useUser();
  /* Responsable de communauté : /me/groups renvoie tout l'arbre du Réseau, donc
     des communautés voisines qu'il n'a pas le droit de cibler. On part de ses
     seuls groupes publiables. */
  const communityScope = scope.mode === 'communities';

  const DATA_SOURCE_URL_MAPPER: Associative = {
    targetGroups: communityScope
      ? '/me/groups/publishable'
      : '/me/groups?per_page=2000',
    targetTopics: '/topics',
    targetDistricts: `/places/districts${
      user?.customer.places
        ? `?town=${user?.customer.places[0]?.address.locality}`
        : ''
    }`,
    targetTowns:
      user?.customer.type === 'department'
        ? '/places/towns?only_department=true'
        : '/places/towns',
  };

  const dataSourceUrl = DATA_SOURCE_URL_MAPPER[name] || '';

  useEffect(() => {
    dataSourceUrl &&
      get<any>(dataSourceUrl, ({ data }) => {
        if (!data) return;

        let selectOptions = [];

        if (name === 'targetDistricts') {
          selectOptions = data.map((district: string) => ({
            label: district,
            value: district,
          }));
        } else if (name === 'targetTowns') {
          selectOptions = Object.entries(data)
            .map(([id, city]) => {
              return { id, city };
            })
            .map((item: any) => ({ label: item.city, value: item.id }));
        } else if (name === 'targetGroups' && communityScope) {
          // Liste plate de ses groupes, sans les voisines ; le Réseau entier seulement
          // pour un évènement, qu'il signe alors au nom d'une de ses communautés.
          selectOptions = [
            ...(withNetworks
              ? eventNetworks(scope.managedGroups)
                  .filter(network => !networkId || network.id === networkId)
                  .map(network => ({
                    label: `🌐 Tout le Réseau ${network.name}`,
                    value: groupTagValue(network),
                  }))
              : []),
            ...data
              .filter(
                (group: any) =>
                  !networkId || (group.root?.id ?? group.id) === networkId
              )
              .map((group: any) => ({
                label:
                  group.lvl >= 2 && group.parent
                    ? `${group.name} · ${group.parent.name}`
                    : group.name,
                value: groupTagValue(group),
              })),
          ];
        } else if (name === 'targetGroups') {
          // Group each network with its communities AND sub-communities via Ant Design OptGroup
          selectOptions = data
            .filter((item: any) => !networkId || item.id === networkId)
            .map((item: any) => ({
              label: `🌐 Réseau · ${item.name}`,
              options: [
                {
                  label: `${item.name} (réseau entier)`,
                  value: groupTagValue(item),
                },
                ...flattenGroupTree([item])
                  .filter(group => group.lvl >= 1)
                  .map(group => ({
                    label: indentLabel(group.name, group.lvl - 1),
                    value: groupTagValue(group),
                  })),
              ],
            }));
        } else {
          selectOptions = data.map((topic: any) => ({
            label: topic.hashtag,
            value: topic.hashtag,
          }));
        }

        /* « Selectionner tout » cible tout le client : hors périmètre d'un
           responsable de communauté. */
        user?.customer.type === CUSTOMER_TYPES.HABITAT &&
          !communityScope &&
          !networkId &&
          selectOptions.unshift({
            label: 'Selectionner tout',
            value: 'all',
          });

        setOptionsValues(selectOptions);

        /* Réseau restreint : retire les cibles déjà choisies ailleurs */
        if (networkId && defaultValue?.length) {
          const allowed = new Set(
            selectOptions.flatMap((option: any) =>
              option.options
                ? option.options.map((child: any) => child.value)
                : [option.value]
            )
          );
          const kept = defaultValue.filter(tag => allowed.has(tag));
          if (kept.length !== defaultValue.length) {
            setSelected(kept);
            onChange && onChange(kept);
          }
        }
      });
    // eslint-disable-next-line
  }, [dataSourceUrl, get, communityScope, withNetworks, networkId]);

  const selectOptions = dataSourceUrl ? optionsValues : options;

  const handleChange = (value: any) => {
    if (value.includes('all')) {
      let allGroups: string[] = [];

      optionsValues.forEach((option: any) => {
        if (option.options?.length) {
          allGroups.push(...option.options.map((option: any) => option.value));
        } else option.value !== 'all' && allGroups.push(option.value);
      });

      setSelected(allGroups);
      onChange && onChange(allGroups);
    } else {
      setSelected(value);
      onChange && onChange(value);
    }
  };

  return (
    <>
      <Text strong={true}>{placeholder}</Text>
      <Select
        id={id}
        value={selected}
        mode="multiple"
        style={{ width: '100%' }}
        onChange={handleChange}
        options={selectOptions}
      />
    </>
  );
};
